import React, { useContext, useEffect, useMemo, useState } from 'react'

import { Box, Icon, Popover, Text } from '@fairfashion/design-system'
import { useLocation, useNavigate } from 'react-router-dom'

import { env } from '../../configs/env.config'
import { AuthContext } from '../../contexts/auth'
import { IBrandsResponseDTO } from '../../sdk/brand/brand.types'
import { CompanySegment } from '../../sdk/common/company.enum'
import { sdk } from '../../services/sdk'
import { routes } from '../../utilities/menu/routes'

import { Breadcrumb } from './components/breadcrumb/breadcrumb.comp'
import * as Styles from './header.styles'
import { HeaderProps } from './header.types'
import { compareRoute } from './header.utils'

export const Header = ({ toggleSidebar }: HeaderProps) => {
  const noSpecifiedBrandChoose = 'Todas as marcas'

  const pathname = useLocation().pathname

  const item = routes.find((route) => compareRoute(route.url ?? '', pathname))

  const navigate = useNavigate()

  const [anchorEl, setAnchorEl] = useState<HTMLElement>()
  const [brands, setBrands] = useState<IBrandsResponseDTO[]>([])
  const [selectedBrand, setSelectedBrand] = useState<string>('')

  const { user } = useContext(AuthContext)

  const isFashionCompany = useMemo(
    () =>
      user?.company?.segment === CompanySegment.FASHION_COMPANY ||
      user?.company?.segment === CompanySegment.BLOCKFORCE,
    [user?.company],
  )

  const closePopover = () => setAnchorEl(undefined)

  const getBrands = async () => {
    try {
      const response = await sdk.brand.getBrands()
      setBrands(response)
    } catch (error) {
      console.error(error)
    }
  }

  const getUserBrandView = async () => {
    try {
      const userResponse = await sdk.user.user()
      const brandFromUser =
        userResponse?.brand?.brandName || noSpecifiedBrandChoose
      setSelectedBrand(brandFromUser)
    } catch (error) {
      console.error(error)
    }
  }

  const handleSelectOnChange = async (selectedValue: string) => {
    selectedValue === noSpecifiedBrandChoose
      ? setSelectedBrand(noSpecifiedBrandChoose)
      : setSelectedBrand(selectedValue)
    closePopover()
    if (selectedValue) {
      try {
        const { token } =
          selectedValue === noSpecifiedBrandChoose
            ? await sdk.authentication.refreshBrandView()
            : await sdk.authentication.refreshBrandView(selectedValue)
        if (token) {
          localStorage.setItem(env.LOCAL_STORAGE_LOGIN_TOKEN, token)
          navigate(0)
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  useEffect(() => {
    getBrands()
    getUserBrandView()
  }, [])

  return (
    <Box height="72px" width="100%" justifyContent="space-between">
      <Box alignItems="center" height="100%" paddingX={3}>
        <Styles.OpenSidebarButton
          onClick={() => toggleSidebar()}
          variant="text"
          padding=""
        >
          <Icon name="menu" size={30} color="white" />
        </Styles.OpenSidebarButton>
        <Box direction="column">
          <Text variant="p" size="large" color="white">
            {item?.title}
          </Text>
          <Breadcrumb />
        </Box>
      </Box>
      {isFashionCompany && (
        <>
          <Box
            alignItems="center"
            height="100%"
            marginRight={4}
            gap={1}
            cursor="pointer"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Text color="white">{selectedBrand}</Text>
            <Icon name="arrow_down_s" size={20} color="white" />
          </Box>
          <Popover
            anchorEl={anchorEl}
            onClose={closePopover}
            position="bottom-end"
            width="220px"
            offset={0}
          >
            {brands.map((b) => {
              return (
                <Styles.PopoverText
                  key={b.name}
                  onClick={() => handleSelectOnChange(b.name)}
                  padding={2}
                >
                  <Box width="100%" justifyContent="space-between">
                    {b.name}
                    {selectedBrand === b.name && (
                      <Icon name="check" size={20} color="secondary" />
                    )}
                  </Box>
                </Styles.PopoverText>
              )
            })}
            <Styles.PopoverText
              onClick={() => handleSelectOnChange(noSpecifiedBrandChoose)}
              padding={2}
            >
              <Box width="100%" justifyContent="space-between">
                {noSpecifiedBrandChoose}
                {selectedBrand === noSpecifiedBrandChoose && (
                  <Icon name="check" size={20} color="secondary" />
                )}
              </Box>
            </Styles.PopoverText>
          </Popover>
        </>
      )}
    </Box>
  )
}
